import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { humanizeApiError } from "../api/client";
import {
  type Dispatch,
  type DispatchListQuery,
  type DispatchStatus,
  dispatchesApi,
} from "../api/dispatches";
import { type User, usersApi } from "../api/users";
import { useAuth } from "../auth/useAuth";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { EmptyTableRow } from "../components/ui/empty-state";
import { Input } from "../components/ui/input";
import { Pagination } from "../components/ui/pagination";
import { Select } from "../components/ui/select";
import { TableSkeleton } from "../components/ui/table-skeleton";

const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 300;

type StatusFilter = "all" | DispatchStatus;

const STATUS_TABS: { value: StatusFilter; label: string; title?: string }[] = [
  { value: "all", label: "Все" },
  {
    value: "active",
    label: "Активные",
    title: "Ссылка действует — получатель может открыть документ",
  },
  {
    value: "expired",
    label: "Истёкшие",
    title: "Срок ссылки прошёл. Можно продлить из карточки отправки",
  },
  {
    value: "disabled",
    label: "Отключённые",
    title: "Менеджер вручную закрыл доступ. Можно включить обратно",
  },
];

function formatDate(iso: string | null): string {
  if (iso === null) return "—";
  return new Date(iso).toLocaleString("ru-RU", {
    dateStyle: "short",
    timeStyle: "short",
  });
}

function extractMessage(err: unknown): string {
  return humanizeApiError(err, { entityLabel: "Отправка" });
}

interface EffectiveStatus {
  label: string;
  tone: string;
}

function effectiveStatus(d: Dispatch): EffectiveStatus {
  if (d.is_disabled) {
    return { label: "Отключена", tone: "bg-red-50 text-red-700" };
  }
  if (d.expires_at && new Date(d.expires_at).getTime() <= Date.now()) {
    return { label: "Истекла", tone: "bg-amber-50 text-amber-800" };
  }
  return { label: "Активна", tone: "bg-emerald-50 text-emerald-700" };
}

export function DispatchesPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [items, setItems] = useState<Dispatch[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [managerId, setManagerId] = useState<string>("");
  const [managers, setManagers] = useState<User[]>([]);

  // Debounce поиска: при каждом изменении строки тред таймер перезаводим.
  useEffect(() => {
    const t = window.setTimeout(() => {
      setDebouncedSearch(search.trim());
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [search]);

  // Список менеджеров для фильтра — только админу. Менеджер видит только
  // свои отправки, фильтр ему не нужен.
  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    void usersApi
      .list({ size: 100, is_active: true })
      .then((r) => {
        if (cancelled) return;
        setManagers(r.items);
      })
      .catch(() => {
        // Не блокируем страницу при ошибке загрузки менеджеров.
      });
    return () => {
      cancelled = true;
    };
  }, [isAdmin]);

  const query = useMemo<DispatchListQuery>(() => {
    const q: DispatchListQuery = { page, size: PAGE_SIZE };
    if (debouncedSearch) q.recipient_q = debouncedSearch;
    if (statusFilter !== "all") q.status = statusFilter;
    if (managerId) q.manager_user_id = managerId;
    return q;
  }, [page, debouncedSearch, statusFilter, managerId]);

  // Сбрасываем страницу на 1 при изменении любого фильтра.
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    setPage(1);
  }, [debouncedSearch, statusFilter, managerId]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    dispatchesApi
      .list(query)
      .then((r) => {
        if (cancelled) return;
        setItems(r.items);
        setTotal(r.total_count);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(extractMessage(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [query]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const hasSearch = debouncedSearch.length > 0;
  const hasManagerFilter = managerId !== "";
  const hasStatusFilter = statusFilter !== "all";

  const headerLabel =
    statusFilter === "all"
      ? "Всего отправок"
      : `Отправок (${STATUS_TABS.find((t) => t.value === statusFilter)?.label.toLowerCase()})`;

  // Текст пустого состояния — зависит от того, какой фильтр привёл к нулю.
  // Раньше любая комбинация фильтров давала «Ничего не нашли по вашему
  // запросу», даже если поискового запроса не было — это сбивало.
  const emptyMessage = (() => {
    if (hasSearch) return "Ничего не нашли по вашему запросу";
    if (hasStatusFilter) {
      const tab = STATUS_TABS.find((t) => t.value === statusFilter);
      const labelLower = tab?.label.toLowerCase();
      return `Нет отправок со статусом «${labelLower}»`;
    }
    if (hasManagerFilter) return "У этого менеджера ещё нет отправок";
    return "Здесь пока ничего нет. Отправки создаются на странице документа.";
  })();

  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <h1 className="text-2xl font-semibold">Отправки</h1>
        <p className="text-sm text-muted-foreground">
          Персональные ссылки на документы, отправленные получателям.
          Здесь видно, кому что отправлено и в каком статусе.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Input
          type="search"
          placeholder="Поиск по email получателя"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-xs"
          aria-label="Поиск по email получателя"
        />
        {isAdmin && managers.length > 0 && (
          <Select
            value={managerId}
            onChange={(e) => setManagerId(e.target.value)}
            className="w-auto"
            aria-label="Фильтр по менеджеру"
          >
            <option value="">Любой менеджер</option>
            {managers.map((m) => (
              <option key={m.id} value={m.id}>
                {m.full_name} ({m.email})
              </option>
            ))}
          </Select>
        )}
        <div
          role="tablist"
          aria-label="Фильтр по статусу"
          className="inline-flex rounded-md border border-input bg-background p-0.5 text-sm"
        >
          {STATUS_TABS.map((tab) => {
            const active = statusFilter === tab.value;
            return (
              <button
                key={tab.value}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setStatusFilter(tab.value)}
                title={tab.title}
                className={
                  "rounded px-3 py-1 transition-colors focus-warm " +
                  (active
                    ? "bg-primary text-primary-foreground glow-warm-sm"
                    : "text-muted-foreground hover:text-foreground hover:bg-accentWarm/10")
                }
              >
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {error && (
        <Card>
          <CardContent className="text-red-700 text-sm py-3">{error}</CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base font-medium">
            {headerLabel}: {total}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-muted-foreground">
              <tr className="border-b">
                <th className="text-left font-normal px-4 py-2">Документ</th>
                <th className="text-left font-normal px-4 py-2">Получатель</th>
                {isAdmin && (
                  <th className="text-left font-normal px-4 py-2">Менеджер</th>
                )}
                <th className="text-left font-normal px-4 py-2">Статус</th>
                <th className="text-left font-normal px-4 py-2">Истекает</th>
                <th className="text-left font-normal px-4 py-2">Создана</th>
              </tr>
            </thead>
            <tbody>
              {loading && items.length === 0 ? (
                <TableSkeleton columns={isAdmin ? 6 : 5} />
              ) : items.length === 0 ? (
                <EmptyTableRow colSpan={isAdmin ? 6 : 5}>
                  {emptyMessage}
                </EmptyTableRow>
              ) : (
                items.map((d) => {
                  const status = effectiveStatus(d);
                  return (
                    <tr key={d.id} className="border-b last:border-b-0 transition-colors hover:bg-accentWarm/10">
                      <td className="px-4 py-2 max-w-xs">
                        <Link
                          to={`/dispatches/${d.id}`}
                          className="text-blue-700 hover:underline line-clamp-2"
                          title={
                            d.document_title
                              ? `${d.document_title}\nИдентификатор: ${d.short_token}`
                              : `Идентификатор: ${d.short_token}`
                          }
                        >
                          {d.document_title ?? d.short_token}
                        </Link>
                      </td>
                      <td className="px-4 py-2">
                        {d.recipient_email ?? (
                          <span className="text-muted-foreground">
                            не указан
                          </span>
                        )}
                      </td>
                      {isAdmin && (
                        <td className="px-4 py-2 text-muted-foreground">
                          {d.manager_email ?? "—"}
                        </td>
                      )}
                      <td className="px-4 py-2">
                        <span
                          className={`inline-block rounded px-2 py-0.5 text-xs ${status.tone}`}
                        >
                          {status.label}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-muted-foreground">
                        {formatDate(d.expires_at)}
                      </td>
                      <td className="px-4 py-2 text-muted-foreground">
                        {formatDate(d.created_at)}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Pagination
        page={page}
        totalPages={totalPages}
        total={total}
        disabled={loading}
        onPageChange={(next) => setPage(next)}
      />
    </div>
  );
}
